import { computed, ref } from "vue";
import {
  createStaffMember,
  deleteStaffMember as apiDeleteStaffMember,
  getStaff,
  updateStaffMember,
} from "../lib/api";

export function useStaff() {
  const staffMembers = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all staff members
   */
  const fetchStaffMembers = async (includeInactive = true) => {
    isLoading.value = true;
    error.value = null;

    try {
      const res = await getStaff(includeInactive);
      staffMembers.value = res.data;
      return res.data;
    } catch (err) {
      error.value = err.message || "Failed to fetch staff members";
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get staff members filtered by store
   */
  const getStaffByStore = (storeId) => {
    if (!storeId) return staffMembers.value;

    return staffMembers.value.filter(
      (staff) => staff.store_ids && staff.store_ids.includes(storeId)
    );
  };

  /**
   * Get staff member by ID
   */
  const getStaffById = (id) => {
    return staffMembers.value.find((staff) => staff.id === id) || null;
  };

  /**
   * Create a mapping of staff IDs to full staff objects
   */
  const createStaffMap = () => {
    const map = new Map();
    for (const member of staffMembers.value) {
      map.set(member.id, member);
    }
    return map;
  };

  /**
   * Get active and inactive staff as computed properties
   */
  const activeStaff = computed(() =>
    staffMembers.value.filter((s) => s.is_active)
  );

  const inactiveStaff = computed(() =>
    staffMembers.value.filter((s) => !s.is_active)
  );

  /**
   * Save a staff member (create or update)
   */
  const saveStaffMember = async (staffData) => {
    isLoading.value = true;
    error.value = null;

    try {
      // Strip out UI-only field before sending to backend
      const { is_admin, ...payload } = staffData;

      if (payload.id) {
        await updateStaffMember(payload.id, payload);
      } else {
        await createStaffMember(payload);
      }

      await fetchStaffMembers();
      return { success: true };
    } catch (err) {
      error.value = err.message || "Failed to save staff member";
      return { success: false, error: error.value };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a staff member
   */
  const deleteStaffMember = async (id, hardDelete = true) => {
    isLoading.value = true;
    error.value = null;

    try {
      await apiDeleteStaffMember(id, hardDelete);
      await fetchStaffMembers();
      return { success: true };
    } catch (err) {
      error.value = err.message || "Failed to delete staff member";
      return { success: false, error: error.value };
    } finally {
      isLoading.value = false;
    }
  };

  return {
    staffMembers,
    activeStaff,
    inactiveStaff,
    isLoading,
    error,
    fetchStaffMembers,
    getStaffByStore,
    getStaffById,
    createStaffMap,
    saveStaffMember,
    deleteStaffMember,
  };
}
